import React, { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import api from '../../services/api';

declare global {
  interface Window {
    L: any;
  }
}

interface LocationWithRadius {
  name: string;
  canonicalName?: string;
  geoTargetId?: string;
  targetType?: string;
  countryCode?: string;
  lat?: number;
  lng?: number;
  radius: number;
}

interface LocationMapProps {
  locations: LocationWithRadius[];
  onLocationCoordsResolved?: (index: number, lat: number, lng: number) => void;
  height?: string;
}

// Cache geocoding results
interface GeoResult {
  lat: number;
  lng: number;
  boundingbox?: [number, number, number, number]; // [south, north, west, east]
}
const geocodeCache: Record<string, GeoResult | null> = {};

const geocodeLocation = async (name: string, countryCode?: string, targetType?: string): Promise<GeoResult | null> => {
  const cacheKey = `${name}_${countryCode || ''}_${targetType || ''}`;
  if (geocodeCache[cacheKey] !== undefined) return geocodeCache[cacheKey];
  try {
    // Geocodifica no backend (Google Geocoding com a chave do servidor, Nominatim
    // de reserva). boundingbox vem como [sul, norte, oeste, leste].
    const res = await api.get('/campaigns/locations/geocode', {
      params: { q: name, country: countryCode || undefined, type: targetType || undefined }
    });
    const result: GeoResult | null = res.data?.data || null;
    if (result && Number.isFinite(result.lat) && Number.isFinite(result.lng)) {
      geocodeCache[cacheKey] = result;
      return result;
    }
  } catch { /* ignore */ }
  geocodeCache[cacheKey] = null;
  return null;
};

const LocationMap: React.FC<LocationMapProps> = ({ locations, onLocationCoordsResolved, height = '300px' }) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const layersRef = useRef<any[]>([]);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [resolvedCount, setResolvedCount] = useState(0);
  const [mapReady, setMapReady] = useState(false);
  const locationsKey = JSON.stringify(locations.map(l => ({ n: l.name, r: l.radius, t: l.targetType })));

  // Init map once — with retry for the async-loaded Leaflet script.
  // Leaflet comes from unpkg via <script> in index.html; depending on latency
  // it may not be available on first mount, so we poll briefly.
  useEffect(() => {
    let pollInterval: ReturnType<typeof setInterval> | null = null;
    let destroyed = false;

    const tryInit = () => {
      if (destroyed) return false;
      const L = window.L;
      const container = mapContainerRef.current;
      if (!L || !container) return false;
      if (mapRef.current) return true;
      // Container collapsed to 0 height (parent still laying out) → wait
      if (container.clientWidth === 0 || container.clientHeight === 0) return false;

      try {
        const map = L.map(container, {
          center: [-14.235, -51.925],
          zoom: 4,
          zoomControl: true,
          attributionControl: false,
          preferCanvas: true,
        });

        // Tiles do OpenStreetMap. O CARTO (basemaps.cartocdn.com) passou a exigir
        // chave e devolvia cada tile com "API KEY REQUIRED" escrito por cima.
        // No tema escuro o OSM não tem versão própria: escurece por filtro CSS.
        const isDark = document.documentElement.classList.contains('dark');
        L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 18,
          className: isDark ? 'osm-tiles-dark' : '',
        }).addTo(map);
        if (isDark) {
          container.style.background = '#1f2937';
          if (!document.getElementById('osm-tiles-dark-style')) {
            const st = document.createElement('style');
            st.id = 'osm-tiles-dark-style';
            st.textContent = '.osm-tiles-dark{filter:invert(1) hue-rotate(180deg) brightness(.9) contrast(.9);}';
            document.head.appendChild(st);
          }
        }

        L.control.attribution({ prefix: false, position: 'bottomright' })
          .addAttribution('<a href="https://osm.org" target="_blank" style="font-size:10px">OSM</a>')
          .addTo(map);

        mapRef.current = map;
        map.whenReady(() => setMapReady(true));
        // Force a resize tick — fixes cases where Leaflet initializes before
        // the container finishes its CSS transition/layout.
        setTimeout(() => { try { map.invalidateSize(); } catch { /* noop */ } }, 200);
        return true;
      } catch (err) {
        console.warn('[LocationMap] init failed:', err);
        return false;
      }
    };

    if (!tryInit()) {
      // Poll every 250ms up to 10s for Leaflet script + container readiness
      let elapsed = 0;
      pollInterval = setInterval(() => {
        elapsed += 250;
        if (tryInit() || elapsed > 10000) {
          if (pollInterval) clearInterval(pollInterval);
          pollInterval = null;
        }
      }, 250);
    }

    return () => {
      destroyed = true;
      if (pollInterval) clearInterval(pollInterval);
      setMapReady(false);
      if (mapRef.current) {
        try { mapRef.current.remove(); } catch { /* noop */ }
        mapRef.current = null;
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Draw locations
  useEffect(() => {
    if (!mapReady) return;

    const L = window.L;
    const map = mapRef.current;
    if (!L || !map) return;

    // Clear old layers (markers, circles, rectangles) — the map base/tiles stay.
    layersRef.current.forEach(layer => {
      try { map.removeLayer(layer); } catch { /* ignore */ }
    });
    layersRef.current = [];

    if (locations.length === 0) {
      // Recenter to default view so next add doesn't open zoomed into nothing
      try { map.setView([-14.235, -51.925], 4); } catch { /* ignore */ }
      return;
    }

    let cancelled = false;
    setIsGeocoding(true);
    setResolvedCount(0);

    const drawLocations = async () => {
      const allBounds: any[] = [];
      let resolved = 0;

      for (let i = 0; i < locations.length; i++) {
        if (cancelled) return;

        const loc = locations[i];
        let geoResult: GeoResult | null = loc.lat && loc.lng ? { lat: loc.lat, lng: loc.lng } : null;

        if (!geoResult) {
          const searchName = loc.canonicalName || loc.name;
          geoResult = await geocodeLocation(searchName, loc.countryCode, loc.targetType);
          if (geoResult && onLocationCoordsResolved) {
            onLocationCoordsResolved(i, geoResult.lat, geoResult.lng);
          }
        }

        if (cancelled || !mapRef.current) return;
        if (!geoResult) continue;

        resolved++;
        setResolvedCount(resolved);

        const isCountry = loc.targetType === 'Country';
        const isState = loc.targetType === 'Province' || loc.targetType === 'State' || loc.targetType === 'Region';
        const radiusKm = loc.radius || 0;
        const latLng = L.latLng(geoResult.lat, geoResult.lng);

        try {
          if (isCountry || (isState && radiusKm === 0)) {
            // For countries and states: draw a rectangle covering the entire area using boundingbox
            if (geoResult.boundingbox) {
              const [south, north, west, east] = geoResult.boundingbox;
              const bounds = L.latLngBounds(
                L.latLng(south, west),
                L.latLng(north, east)
              );

              const rect = L.rectangle(bounds, {
                color: '#6366f1',
                fillColor: '#818cf8',
                fillOpacity: 0.12,
                weight: 2,
                dashArray: isCountry ? '' : '6, 4',
              }).addTo(map);
              layersRef.current.push(rect);

              allBounds.push(bounds);

              // Label at center
              const center = bounds.getCenter();
              const label = L.marker(center, {
                icon: L.divIcon({
                  className: 'leaflet-label-custom',
                  html: `<div style="background:#6366f1;color:#fff;padding:3px 10px;border-radius:12px;font-size:12px;font-weight:600;white-space:nowrap;box-shadow:0 2px 6px rgba(0,0,0,.25);border:2px solid #fff">${loc.name}${isState ? ' (Estado)' : ''}</div>`,
                  iconSize: [0, 0],
                  iconAnchor: [0, 0],
                })
              }).addTo(map);
              layersRef.current.push(label);
            } else {
              // Fallback: large circle if no boundingbox
              const fallbackRadius = isCountry ? 2000000 : 300000;
              const circle = L.circle(latLng, {
                radius: fallbackRadius,
                color: '#6366f1',
                fillColor: '#818cf8',
                fillOpacity: 0.1,
                weight: 2,
                dashArray: '8, 6',
              }).addTo(map);
              layersRef.current.push(circle);
              allBounds.push(latLng);

              const label = L.marker(latLng, {
                icon: L.divIcon({
                  className: 'leaflet-label-custom',
                  html: `<div style="background:#6366f1;color:#fff;padding:3px 10px;border-radius:12px;font-size:12px;font-weight:600;white-space:nowrap;box-shadow:0 2px 6px rgba(0,0,0,.25);border:2px solid #fff">${loc.name}</div>`,
                  iconSize: [0, 0],
                  iconAnchor: [0, 0],
                })
              }).addTo(map);
              layersRef.current.push(label);
            }
          } else if (radiusKm > 0) {
            // City/region with custom radius
            const circle = L.circle(latLng, {
              radius: radiusKm * 1000,
              color: '#6366f1',
              fillColor: '#6366f1',
              fillOpacity: 0.15,
              weight: 2,
            }).addTo(map);
            layersRef.current.push(circle);
            allBounds.push(latLng);

            const label = L.marker(latLng, {
              icon: L.divIcon({
                className: 'leaflet-label-custom',
                html: `<div style="background:#6366f1;color:#fff;padding:2px 8px;border-radius:12px;font-size:11px;font-weight:600;white-space:nowrap;box-shadow:0 2px 6px rgba(0,0,0,.25);border:2px solid #fff">${loc.name.split(',')[0]} · ${radiusKm}km</div>`,
                iconSize: [0, 0],
                iconAnchor: [0, 0],
              })
            }).addTo(map);
            layersRef.current.push(label);

          } else {
            // City/point without radius
            const circle = L.circle(latLng, {
              radius: 30000,
              color: '#6366f1',
              fillColor: '#818cf8',
              fillOpacity: 0.2,
              weight: 2,
            }).addTo(map);
            layersRef.current.push(circle);
            allBounds.push(latLng);

            const marker = L.circleMarker(latLng, {
              radius: 6,
              color: '#ffffff',
              fillColor: '#6366f1',
              fillOpacity: 1,
              weight: 2,
            }).addTo(map);
            layersRef.current.push(marker);

            const label = L.marker(latLng, {
              icon: L.divIcon({
                className: 'leaflet-label-custom',
                html: `<div style="background:#6366f1;color:#fff;padding:2px 8px;border-radius:12px;font-size:11px;font-weight:600;white-space:nowrap;box-shadow:0 2px 6px rgba(0,0,0,.25);border:2px solid #fff;transform:translateY(-20px)">${loc.name.split(',')[0]}</div>`,
                iconSize: [0, 0],
                iconAnchor: [0, -10],
              })
            }).addTo(map);
            layersRef.current.push(label);
          }
        } catch (err) {
          console.warn('Failed to draw location on map:', err);
        }
      }

      if (!cancelled && mapRef.current && allBounds.length > 0) {
        try {
          // Merge all bounds
          let finalBounds = allBounds[0];
          for (let i = 1; i < allBounds.length; i++) {
            const b = allBounds[i];
            if (b.extend) {
              // It's a LatLngBounds, extend finalBounds
              if (finalBounds.extend) finalBounds.extend(b);
              else finalBounds = L.latLngBounds(finalBounds, b.getNorthEast()).extend(b.getSouthWest());
            } else {
              // It's a LatLng point
              if (finalBounds.extend) finalBounds.extend(b);
              else finalBounds = L.latLngBounds(finalBounds, b);
            }
          }
          if (finalBounds.isValid ? finalBounds.isValid() : true) {
            map.fitBounds(finalBounds, { padding: [30, 30], maxZoom: 12 });
          }
        } catch { /* ignore */ }
      }
      setIsGeocoding(false);
    };

    drawLocations();

    return () => { cancelled = true; };
  }, [mapReady, locationsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Invalidate size on common layout-change triggers. Leaflet renders a canvas
  // that doesn't auto-resize, so if the container grows (sidebar toggle, tab
  // switch, window resize), we need to tell Leaflet to recompute.
  useEffect(() => {
    if (!mapReady) return;
    const timer = setTimeout(() => {
      try { mapRef.current?.invalidateSize(); } catch { /* ignore */ }
    }, 300);
    return () => clearTimeout(timer);
  }, [mapReady, locations.length]);

  useEffect(() => {
    if (!mapReady) return;
    const handleResize = () => {
      try { mapRef.current?.invalidateSize(); } catch { /* ignore */ }
    };
    window.addEventListener('resize', handleResize);
    // ResizeObserver fires when the container itself changes size (parent
    // panel toggled, accordion expands, etc.) — covers cases where window
    // size didn't change but the map's box did.
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined' && mapContainerRef.current) {
      ro = new ResizeObserver(() => handleResize());
      ro.observe(mapContainerRef.current);
    }
    return () => {
      window.removeEventListener('resize', handleResize);
      if (ro) ro.disconnect();
    };
  }, [mapReady]);

  // NEVER unmount when locations is empty — the map instance must be kept
  // alive across add/remove cycles, otherwise Leaflet's init races with React
  // re-mounts and the canvas fails to render. We just hide the info pills.
  const hasLocations = locations.length > 0;
  const countriesCount = locations.filter(l => l.targetType === 'Country').length;
  const statesCount = locations.filter(l => l.targetType === 'Province' || l.targetType === 'State' || l.targetType === 'Region').length;
  const citiesCount = locations.filter(l => l.targetType !== 'Country' && l.targetType !== 'Province' && l.targetType !== 'State' && l.targetType !== 'Region').length;
  const withRadius = locations.filter(l => l.radius > 0);

  return (
    <div className="relative isolate rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 mt-3">
      {/* Loading overlay shown until the map init completes (covers the window
          where Leaflet is still being fetched from the CDN). */}
      {!mapReady && (
        <div className="absolute inset-0 z-[999] flex items-center justify-center bg-gray-50 dark:bg-gray-900/80 backdrop-blur-sm">
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <Loader2 className="w-4 h-4 animate-spin" />
            Carregando mapa...
          </div>
        </div>
      )}
      {/* Empty state — shown ON TOP of the map when there are no locations.
          The map itself keeps rendered underneath so we don't re-init on next add. */}
      {mapReady && !hasLocations && (
        <div className="absolute inset-0 z-[998] flex items-center justify-center bg-gray-50/80 dark:bg-gray-900/70 backdrop-blur-[2px] pointer-events-none">
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center px-4">
            Adicione ao menos uma localização pra visualizar no mapa
          </p>
        </div>
      )}
      {isGeocoding && hasLocations && (
        <div className="absolute top-2 right-2 z-[1000] flex items-center gap-1.5 bg-white dark:bg-gray-800 rounded-lg px-2.5 py-1 shadow-md text-xs text-gray-600 dark:text-gray-300">
          <Loader2 className="w-3 h-3 animate-spin" />
          {resolvedCount}/{locations.length}
        </div>
      )}
      <div className={`absolute bottom-2 left-2 z-[1000] flex items-center gap-1.5 flex-wrap ${hasLocations ? '' : 'hidden'}`}>
        {countriesCount > 0 && (
          <span className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-lg px-2 py-0.5 text-[10px] font-medium text-gray-600 dark:text-gray-300 shadow-sm">
            {countriesCount} {countriesCount === 1 ? 'país' : 'países'}
          </span>
        )}
        {statesCount > 0 && (
          <span className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-lg px-2 py-0.5 text-[10px] font-medium text-gray-600 dark:text-gray-300 shadow-sm">
            {statesCount} {statesCount === 1 ? 'estado' : 'estados'}
          </span>
        )}
        {citiesCount > 0 && (
          <span className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-lg px-2 py-0.5 text-[10px] font-medium text-gray-600 dark:text-gray-300 shadow-sm">
            {citiesCount} {citiesCount === 1 ? 'cidade' : 'cidades'}
          </span>
        )}
        {withRadius.length > 0 && (
          <span className="bg-primary-100/90 dark:bg-primary-900/60 backdrop-blur-sm rounded-lg px-2 py-0.5 text-[10px] font-medium text-primary-700 dark:text-primary-300 shadow-sm">
            {withRadius.length} com raio
          </span>
        )}
      </div>
      <div
        ref={mapContainerRef}
        style={{ height, width: '100%' }}
        className="bg-gray-100 dark:bg-gray-800"
      />
    </div>
  );
};

export default LocationMap;
